import type {
  WordRun,
  ParsedWordSegmentWithDirection,
  TextDirection,
} from '../types';

/**
 * Phase 6: Run Reordering
 *
 * Apply visual reordering to runs based on embedding levels and directions.
 * Includes logic to determine when global reordering should be applied.
 */
export const reorderRuns = (
  runs: Array<WordRun>,
  paragraphDirection: TextDirection,
): Array<WordRun> => {
  if (
    !shouldApplyGlobalReordering(
      runs.flatMap(r => r.words),
      paragraphDirection,
    )
  ) {
    return runs;
  }

  // Clone the runs array to avoid mutation
  let reorderedRuns = [...runs];

  // First, reverse words within RTL runs for proper word-level ordering
  reorderedRuns = reorderedRuns.map(run => {
    if (run.direction === 'rtl') {
      // Reverse the words within this RTL run
      const reversedWords = [...run.words].reverse();
      return {
        ...run,
        words: reversedWords,
      };
    }
    return run;
  });

  // Find the maximum embedding level
  const maxLevel = Math.max(...runs.map(run => run.embeddingLevel));

  // For each level from max down to 1, reverse consecutive runs at that level
  for (let level = maxLevel; level >= 1; level--) {
    let start = 0;

    while (start < reorderedRuns.length) {
      // Find the start of a sequence at this level
      const currentStart = start;
      const foundIndex = reorderedRuns.findIndex(
        (run, index) => index >= currentStart && run.embeddingLevel >= level,
      );

      if (foundIndex === -1) {
        break;
      }

      start = foundIndex;

      // Find the end of the sequence at this level
      const currentEnd = start;
      const endIndex = reorderedRuns.findIndex(
        (run, index) => index > currentEnd && run.embeddingLevel < level,
      );

      const end = endIndex === -1 ? reorderedRuns.length : endIndex;

      // Reverse the sequence if it has runs at the current level
      if (end > start) {
        const sequence = reorderedRuns.slice(start, end);
        sequence.reverse();
        reorderedRuns.splice(start, end - start, ...sequence);
      }

      start = end;
    }
  }

  return reorderedRuns;
};

/**
 * Determine if text should be globally reordered:
 * 1. We have mixed content (both RTL and LTR words), OR
 * 2. Paragraph direction is RTL, OR
 * 3. We have pure RTL content (RTL text should be reordered regardless of paragraph direction)
 */
const shouldApplyGlobalReordering = (
  words: Array<ParsedWordSegmentWithDirection>,
  paragraphDirection: TextDirection,
): boolean => {
  const rtlWords = words.filter(
    w => w.isWordLike && w.direction === 'rtl',
  ).length;
  const ltrWords = words.filter(
    w => w.isWordLike && w.direction === 'ltr',
  ).length;
  const totalWords = rtlWords + ltrWords;

  if (totalWords === 0) {
    return false;
  }

  const hasMixedContent = rtlWords > 0 && ltrWords > 0;
  const isRTLParagraph = paragraphDirection === 'rtl';
  const hasPureRTLContent = rtlWords > 0 && ltrWords === 0;

  return hasMixedContent || isRTLParagraph || hasPureRTLContent;
};
